import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/start-a-business-in-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/start-a-business-in-colorado")({
  head: () => ({
    meta: [
        { name: "description", content: "Step-by-step 2026 guide to starting a business in Colorado: registration, licensing, website, and getting found online from a Colorado Springs agency." },
      {
        title: "How to Start a Business in Colorado — 2026 Guide",
      },
      {
        name: "description",
        content:
          "Step-by-step: how to register an LLC, get your Colorado business license, file with the Secretary of State — and the online setup that actually brings in customers.",
      },
      {
        property: "og:title",
        content:
          "How to Start a Business in Colorado (and Get Found Online)",
      },
      {
        property: "og:description",
        content:
          "Registration, licensing, and the digital foundation new Colorado businesses need to start getting calls — from a 25-year local agency.",
      },
      {
        property: "og:url",
        content: `${SITE_URL}/learn/start-a-business-in-colorado`,
      },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title:
          ARTICLE?.title ??
          "How to Start a Business in Colorado (and Get Found Online)",
        description:
          ARTICLE?.desc ??
          "Registration, licensing, and the online setup new Colorado businesses need.",
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coloradowebimpressions.com/learn/start-a-business-in-colorado",
      },
    ],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="12 min read"
      title="How to start a business in Colorado (and actually get found online)"
      intro="Two halves to launching a Colorado business in 2026: the paperwork (Secretary of State, licenses, taxes) and the part most new owners under-invest in — the online setup that decides whether the phone ever rings. Here&rsquo;s both, in plain English."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Start a business in Colorado" },
      ]}
      path="/learn/start-a-business-in-colorado"
      faqs={[
        {
          q: "How much does it cost to register a business in Colorado?",
          a: "Filing an LLC with the Colorado Secretary of State costs $50 online. The annual Periodic Report is $25. Trade name (DBA) registration is $20. A local business license varies by city — Colorado Springs, Denver, and most Front Range cities charge $25–$100/year.",
        },
        {
          q: "Do I need a business license in Colorado?",
          a: "Colorado has no general statewide business license, but most cities require one (Denver, Colorado Springs, Aurora, Boulder, Fort Collins all do). You may also need a state sales tax license if you sell taxable goods or services, and industry-specific licenses (contractor, food service, cosmetology, etc.).",
        },
        {
          q: "LLC or sole proprietorship for a new Colorado business?",
          a: "Most service businesses we work with form an LLC. It separates personal and business liability for $50 + $25/year, and Colorado&rsquo;s filing process is one of the simplest in the country. Sole prop is free but offers zero liability protection.",
        },
        {
          q: "How long does it take to register a business in Colorado?",
          a: "The Colorado Secretary of State LLC filing is usually approved within minutes online. EIN from the IRS is same-day. Local business license takes 1–4 weeks depending on the city. You can be legally open in under a week.",
        },
        {
          q: "When should I build the website?",
          a: "Before you start spending on marketing. A working website + Google Business Profile is the foundation everything else (ads, SEO, referrals) points to. Launching marketing without them is paying to send people to a broken front door.",
        },
      ]}
    >
      <h2>Part 1: The paperwork (about a week)</h2>

      <h3>1. Pick a business structure</h3>
      <p>
        For 90% of the Colorado small businesses we work with, the choice is{" "}
        <strong>LLC</strong>. It costs $50 to file, takes 10 minutes online, and
        legally separates you from the business. Sole proprietorship is free
        but offers no liability protection — one lawsuit and your personal
        assets are exposed. S-corps and C-corps make sense later if you scale,
        but day one, LLC is the default.
      </p>

      <h3>2. File with the Colorado Secretary of State</h3>
      <p>
        Go to{" "}
        <a
          href="https://www.coloradosos.gov/biz/"
          target="_blank"
          rel="noopener noreferrer"
        >
          coloradosos.gov/biz
        </a>
        . Search the business database first to make sure your name is
        available. Then file the Articles of Organization — $50, paid by card,
        approved within minutes. Save the PDF confirmation; banks and the IRS
        will ask for it.
      </p>
      <p>
        After year one you owe a $25 Periodic Report annually. Miss it for two
        years and the state dissolves your LLC, so put it on your calendar.
      </p>

      <h3>3. Get your EIN from the IRS</h3>
      <p>
        Free, online, takes 10 minutes at{" "}
        <a
          href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"
          target="_blank"
          rel="noopener noreferrer"
        >
          irs.gov
        </a>
        . You need this to open a business bank account and hire anyone.
        Never pay a third-party site for an EIN — they charge $100+ for a free
        form.
      </p>

      <h3>4. Open a business bank account</h3>
      <p>
        Bring your LLC filing, EIN letter, and operating agreement. Keep
        personal and business money separated from day one — co-mingling is
        the single fastest way to lose LLC protection in a lawsuit.
      </p>

      <h3>5. Register for state and local taxes</h3>
      <ul>
        <li>
          <strong>Sales tax license</strong> — required if you sell taxable
          goods or certain services. File through Colorado&rsquo;s{" "}
          <a
            href="https://mybiz.colorado.gov/"
            target="_blank"
            rel="noopener noreferrer"
          >
            MyBizColorado
          </a>{" "}
          one-stop portal.
        </li>
        <li>
          <strong>City business license</strong> — Colorado Springs, Denver,
          Aurora, Boulder, Fort Collins, and most Front Range cities require
          one. $25–$100/year. Search &ldquo;[your city] business license.&rdquo;
        </li>
        <li>
          <strong>Industry licenses</strong> — contractors, electricians,
          plumbers, cosmetologists, food service, childcare, and dozens of
          other trades need state-level licensing through DORA or CDPHE.
        </li>
      </ul>

      <h3>6. Get the right insurance</h3>
      <p>
        General liability minimum. Add professional liability (E&amp;O) if you
        give advice, commercial auto if you drive for work, and workers&rsquo;
        comp the day you hire your first W-2 employee. Colorado requires
        workers&rsquo; comp for almost every employer — there is no
        small-business exemption.
      </p>

      <h2>Part 2: The online setup (this is where most new businesses fail)</h2>
      <p>
        You can be perfectly registered with the state and still get zero
        calls. The reason is simple: when someone in Colorado needs your
        service, they search Google. If you&rsquo;re not there, the legal
        paperwork is just an expensive certificate on the wall.
      </p>

      <h3>7. Claim your Google Business Profile (do this today)</h3>
      <p>
        This is the single highest-ROI thing a new Colorado business can do.
        Free. It&rsquo;s what powers the map pack you see when you search
        &ldquo;plumber near me&rdquo; or &ldquo;dentist Colorado Springs.&rdquo;
        Go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer">business.google.com</a>,
        verify by postcard or video, and fill out every field — hours,
        services, photos, service areas. We have a full{" "}
        <Link to="/learn/google-business-profile">
          Google Business Profile playbook
        </Link>{" "}
        if you want the deep version.
      </p>

      <h3>8. Build a real website (not a one-page DIY)</h3>
      <p>
        Your website is the destination every other channel — Google,
        referrals, business cards, ads — points to. It needs to load fast on a
        phone, clearly say what you do and where, and make it easy to call or
        book. A weekend Wix page rarely clears that bar. For a realistic
        budget breakdown, see{" "}
        <Link to="/learn/web-design-cost-colorado">
          what a Colorado business website should cost
        </Link>
        .
      </p>

      <h3>9. Set up local SEO from day one</h3>
      <p>
        Don&rsquo;t wait six months. The basics — consistent name/address/phone
        across directories, city-specific pages on your site, a steady review
        flow — compound. Starting them at launch means you&rsquo;re ranking by
        month 3 instead of month 12. Our{" "}
        <Link to="/learn/local-seo-guide">
          plain-English local SEO guide
        </Link>{" "}
        walks through what actually matters.
      </p>

      <h3>10. Decide on SEO vs. Google Ads</h3>
      <p>
        New businesses often need both — Ads for revenue this month, SEO for
        leads next year. We break down the trade-offs by budget in{" "}
        <Link to="/learn/seo-vs-ppc">SEO vs. Google Ads for Colorado small businesses</Link>
        .
      </p>

      <h3>11. Get reviews from day one</h3>
      <p>
        Every job, every client, every transaction — ask. Reviews are the
        single biggest ranking signal for Google Maps and the biggest
        conversion signal for your website. Our{" "}
        <Link to="/learn/get-more-reviews">
          guide to getting more Google reviews
        </Link>{" "}
        has the exact ask-template we use.
      </p>

      <h3>12. Track what works</h3>
      <p>
        Hook up call tracking and form tracking from launch. If you can&rsquo;t
        see which channel drove which lead, you&rsquo;ll waste 50%+ of your
        marketing budget within a year. This is also where you set a realistic{" "}
        <Link to="/learn/marketing-budget">marketing budget</Link>.
      </p>

      <h2>A realistic timeline for a new Colorado business</h2>
      <ul>
        <li>
          <strong>Week 1</strong> — LLC filed, EIN issued, bank account open,
          domain bought, Google Business Profile claimed.
        </li>
        <li>
          <strong>Weeks 2–4</strong> — Website live, sales tax + city license
          handled, insurance in place, first 5 reviews requested.
        </li>
        <li>
          <strong>Months 2–3</strong> — Local SEO foundation in place
          (citations, on-page, content), first ad campaigns testing.
        </li>
        <li>
          <strong>Months 4–6</strong> — Map pack rankings start moving,
          referrals compounding, you have data to decide where to scale spend.
        </li>
      </ul>

      <h2>Free strategy call for new Colorado businesses</h2>
      <p>
        If you&rsquo;re launching in Colorado Springs, Denver, or anywhere on
        the Front Range and want a 30-minute no-pressure walkthrough of what
        your specific business actually needs online — book a free strategy
        call with Chris, the owner. He&rsquo;ll tell you the honest order of
        operations for your industry, what to do yourself, and where (if
        anywhere) it makes sense to hire help.
      </p>
      <p>
        <Link
          to="/contact"
          search={{ topic: "Free strategy call — new Colorado business" }}
          className="inline-flex items-center justify-center rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
        >
          Book your free strategy call →
        </Link>
      </p>
    </ArticleLayout>
  );
}
